import type { UpgradeDefinition } from '@/types/game';

// Tree layout — 5 clean chains from golpe_elfico (root), no cross-branch connections:
//
//  manos_maestras(18,24)       canto_yunque(50,14)      consejo_capitanes(82,24)
//       |                            |                           |
//  forja_ligera(18,38)         vetas_profundas(50,28)    entrenamiento(82,38)
//       |                            |                           |
//  diezmo_erebor(20,52)        arena_estelar(54,44)      cronicas(82,52)
//       |                            |                           |
//  tesoros_antiguos(24,68)     tiempo_prestado(54,58)   sabiduria_eldar(76,68)
//        \          filo_lunar(44,72)  /  \              /
//                     \     /                \          /
//                      golpe_elfico(50,84)

export const UPGRADES: readonly UpgradeDefinition[] = [
  // ── ROOT ──────────────────────────────────────────────────────────────────
  {
    id: 'golpe_elfico',
    name: 'Golpe élfico',
    shortName: 'Golpe',
    desc: '+6% daño de click por rango.',
    maxRank: 4,
    baseCost: 6,
    costGrowth: 1.65,
    effect: 'click_damage_pct',
    valuePerRank: 0.06,
    position: { x: 50, y: 92 },
    branch: 'damage',
  },

  // ── DAMAGE chain (left-center) ────────────────────────────────────────────
  {
    id: 'filo_lunar',
    name: 'Filo lunar',
    shortName: 'Filo',
    desc: '+4% daño de click por rango.',
    maxRank: 4,
    baseCost: 12,
    costGrowth: 1.75,
    effect: 'click_damage_pct',
    valuePerRank: 0.04,
    requires: { golpe_elfico: 2 },
    position: { x: 25, y: 92 },
    branch: 'damage',
  },

  // ── WEALTH chain (left) ───────────────────────────────────────────────────
  {
    id: 'tesoros_antiguos',
    name: 'Tesoros antiguos',
    shortName: 'Oro',
    desc: '+3% oro por kill por rango.',
    maxRank: 4,
    baseCost: 5,
    costGrowth: 1.6,
    effect: 'gold_pct',
    valuePerRank: 0.03,
    requires: { golpe_elfico: 1 },
    position: { x: 34, y: 59 },
    branch: 'wealth',
  },
  {
    id: 'diezmo_erebor',
    name: 'Diezmo de Erebor',
    shortName: 'Botín',
    desc: '+2% oro por kill por rango.',
    maxRank: 5,
    baseCost: 11,
    costGrowth: 1.68,
    effect: 'gold_pct',
    valuePerRank: 0.02,
    requires: { tesoros_antiguos: 2 },
    position: { x: 14, y: 55 },
    branch: 'wealth',
  },
  {
    id: 'forja_ligera',
    name: 'Forja ligera',
    shortName: 'Coste',
    desc: '-3% coste de entrenar compañeros por rango.',
    maxRank: 4,
    baseCost: 13,
    costGrowth: 1.75,
    effect: 'companion_cost_pct',
    valuePerRank: -0.03,
    requires: { diezmo_erebor: 2 },
    position: { x: 17, y: 38 },
    branch: 'companions',
  },
  {
    id: 'manos_maestras',
    name: 'Manos maestras',
    shortName: 'Taller',
    desc: '-2% coste de entrenar compañeros por rango.',
    maxRank: 3,
    baseCost: 28,
    costGrowth: 1.9,
    effect: 'companion_cost_pct',
    valuePerRank: -0.02,
    requires: { forja_ligera: 3 },
    position: { x: 32, y: 28 },
    branch: 'companions',
  },

  // ── WISDOM chain (right) ──────────────────────────────────────────────────
  {
    id: 'sabiduria_eldar',
    name: 'Sabiduría Eldar',
    shortName: 'XP',
    desc: '+3% experiencia por rango.',
    maxRank: 4,
    baseCost: 5,
    costGrowth: 1.6,
    effect: 'xp_pct',
    valuePerRank: 0.03,
    requires: { golpe_elfico: 1 },
    position: { x: 65, y: 59 },
    branch: 'wisdom',
  },
  {
    id: 'cronicas_imladris',
    name: 'Crónicas de Imladris',
    shortName: 'Saber',
    desc: '+2% experiencia por rango.',
    maxRank: 5,
    baseCost: 11,
    costGrowth: 1.68,
    effect: 'xp_pct',
    valuePerRank: 0.02,
    requires: { sabiduria_eldar: 2 },
    position: { x: 85, y: 55 },
    branch: 'wisdom',
  },
  {
    id: 'entrenamiento_compania',
    name: 'Entrenamiento de la Compañía',
    shortName: 'Cap',
    desc: '+1 al cap de nivel de compañeros por rango.',
    maxRank: 2,
    baseCost: 16,
    costGrowth: 2,
    effect: 'companion_cap',
    valuePerRank: 1,
    requires: { cronicas_imladris: 2 },
    position: { x: 83, y: 38 },
    branch: 'companions',
  },
  {
    id: 'consejo_capitanes',
    name: 'Consejo de capitanes',
    shortName: 'Mando',
    desc: '+1 al cap de nivel de compañeros por rango.',
    maxRank: 1,
    baseCost: 38,
    costGrowth: 2.2,
    effect: 'companion_cap',
    valuePerRank: 1,
    requires: { entrenamiento_compania: 2 },
    position: { x: 68, y: 28 },
    branch: 'companions',
  },

  // ── TIME chain (center-right) → MITHRIL chain (center-top) ───────────────
  {
    id: 'tiempo_prestado',
    name: 'Tiempo prestado',
    shortName: 'Tiempo',
    desc: '+2s al timer de semi-jefes y jefes por rango.',
    maxRank: 4,
    baseCost: 14,
    costGrowth: 1.8,
    effect: 'fight_time_s',
    valuePerRank: 2,
    requires: { golpe_elfico: 1 },
    position: { x: 50, y: 67 },
    branch: 'time',
  },
  {
    id: 'arena_estelar',
    name: 'Arena estelar',
    shortName: 'Reloj',
    desc: '+1s al timer de semi-jefes y jefes por rango.',
    maxRank: 5,
    baseCost: 20,
    costGrowth: 1.75,
    effect: 'fight_time_s',
    valuePerRank: 1,
    requires: { tiempo_prestado: 2 },
    position: { x: 50, y: 49 },
    branch: 'time',
  },
  {
    id: 'vetas_profundas',
    name: 'Vetas profundas',
    shortName: 'Mithril',
    desc: '+0,5 mithril al derrotar semi-jefes y jefes por rango.',
    maxRank: 4,
    baseCost: 18,
    costGrowth: 1.85,
    effect: 'mithril_flat',
    valuePerRank: 0.5,
    requires: { arena_estelar: 2 },
    position: { x: 50, y: 33 },
    branch: 'mithril',
  },
  {
    id: 'canto_yunque',
    name: 'Canto del yunque',
    shortName: 'Yunque',
    desc: '+0,5 mithril al derrotar semi-jefes y jefes por rango.',
    maxRank: 4,
    baseCost: 34,
    costGrowth: 2,
    effect: 'mithril_flat',
    valuePerRank: 0.5,
    requires: { vetas_profundas: 3 },
    position: { x: 50, y: 15 },
    branch: 'mithril',
  },
];

export const UPGRADE_BY_ID = Object.fromEntries(UPGRADES.map((upgrade) => [upgrade.id, upgrade]));
